import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Clock, Package, FileText, PlayCircle, Receipt, ChevronRight, Check, Circle, Sparkles, Send, Shield, Rocket, Loader2, KeyRound, HardHat, Lock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { api } from '../../utils/api';
import { toast } from 'sonner';

// Projekt-wizard som speglar den autonoma projektmotorn:
//   Geofence → Material (+Verktyg) → Arbete → Tid → Offert → Kör & stäng
// Klick på ett steg växlar rätt flik via onSwitchTab. Steg låses tills
// föregående är klart — man kan inte beräkna arbete innan materialet är klart.
const STEPS = [
  { key: 'material', label: 'Material & Verktyg', tab: 'material-plan', icon: Package,
    hint: 'Ladda upp ritning + beskriv. AI delar in i byggfaser + listar material. Markera verktyg som finns i lager.' },
  { key: 'labor', label: 'Arbete & Bemanning', tab: 'labor-plan', icon: HardHat,
    hint: 'AI allokerar yrken, timmar och maskiner per byggfas med dina löner.' },
  { key: 'time', label: 'Tid & Schema', tab: 'time-plan', icon: Clock,
    hint: 'AI räknar projektlängd + Gantt-schema + risk för fördröjning per fas.' },
  { key: 'quote', label: 'Översikt & Offert', tab: 'overview', icon: FileText,
    hint: 'Sammanställer material + arbete + tid till en offert. Granska innan du skickar.' },
  { key: 'execute', label: 'Kör & Fakturera', tab: 'time-reports', icon: PlayCircle,
    hint: 'Anställda stämplar in, ÄTA dokumenteras, fakturan signeras och projektet stängs.' },
];

const StepStatusIcon = ({ done, current, locked }) => {
  if (done) return <Check size={16} className="text-white" />;
  if (current) return <Sparkles size={16} className="text-white" />;
  if (locked) return <Lock size={12} className="text-muted-foreground/40" />;
  return <Circle size={12} className="text-muted-foreground/50" />;
};

const ProjectWizard = ({ project, projectStatus, onSwitchTab, onProjectStarted }) => {
  const [generatingQuote, setGeneratingQuote] = useState(false);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [quote, setQuote] = useState(null);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [startPin, setStartPin] = useState('');
  const [starting, setStarting] = useState(false);
  const cardRef = useRef(null);
  const prevNextIdxRef = useRef(null);

  // Härled status per steg utifrån projekt-data
  const status = {
    material: !!(projectStatus?.materialPlanCount > 0),
    labor: !!(projectStatus?.laborPlanCount > 0),
    time: !!(projectStatus?.timePlanCount > 0 || project?.ai_time_estimate || projectStatus?.hasTimeEstimate),
    quote: !!(project?.quote_generated_at),
    execute: !!(projectStatus?.timeReportCount > 0),
  };

  // Nästa steg = första odone
  const nextIdx = STEPS.findIndex(s => !status[s.key]);
  const currentIdx = nextIdx === -1 ? STEPS.length - 1 : nextIdx;
  const allDone = nextIdx === -1;

  // Auto-scroll + toast när nästa steg triggas (dvs föregående klarades)
  useEffect(() => {
    if (prevNextIdxRef.current !== null && prevNextIdxRef.current !== currentIdx && !allDone) {
      const doneStep = STEPS[prevNextIdxRef.current];
      if (doneStep) {
        toast.success(`✅ ${doneStep.label} — klart! Nästa: ${STEPS[currentIdx].label}`);
      }
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    prevNextIdxRef.current = currentIdx;
  }, [currentIdx, allDone]);

  const handleGenerateQuote = async () => {
    setGeneratingQuote(true);
    try {
      const res = await api.generateProjectQuote(project.id);
      setQuote(res.data);
      toast.success('Offert genererad!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte generera offert');
    } finally { setGeneratingQuote(false); }
  };

  const handleSendQuote = async (mode) => {
    setSendingQuote(true);
    try {
      const res = await api.sendProjectQuote(project.id, { mode });
      if (mode === 'signed' && res.data.signing_url) {
        window.open(res.data.signing_url, '_blank');
        toast.success('Signeringslänk öppnad. Kunden signerar med BankID/PIN.');
      } else {
        toast.success('Offert mejlad till kunden!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skicka offert');
    } finally { setSendingQuote(false); }
  };

  const handleStartProject = async () => {
    if (!/^\d{4,8}$/.test(startPin)) {
      toast.error('PIN måste vara 4–8 siffror');
      return;
    }
    setStarting(true);
    try {
      await api.startProject(project.id, { pin: startPin });
      toast.success('🚀 Projektet är startat och låst för redigering!');
      setShowStartDialog(false);
      setStartPin('');
      onProjectStarted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte starta projekt');
    } finally { setStarting(false); }
  };

  return (
    <div className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 border border-orange-200 dark:border-orange-900 rounded-xl p-5 mb-6" data-testid="project-wizard">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-bold flex items-center gap-2">
            <Sparkles size={16} className="text-[#E8613A]" /> Operio-guide
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {nextIdx === -1
              ? 'Alla steg klara — bra jobbat!'
              : `Nästa steg: ${STEPS[nextIdx].label}`}
          </p>
        </div>
        <div className="text-xs text-muted-foreground">
          Steg {Math.min(currentIdx + 1, STEPS.length)} / {STEPS.length}
        </div>
      </div>

      {/* Steg-progress */}
      <div className="grid grid-cols-6 gap-2 mb-4">
        {STEPS.map((s, i) => {
          const isDone = status[s.key];
          const isCurrent = i === currentIdx && !isDone;
          const isLocked = !isDone && !isCurrent && i > currentIdx;
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              onClick={() => {
                if (isLocked) {
                  toast.info(`Klara steg ${currentIdx + 1} "${STEPS[currentIdx].label}" först`);
                  return;
                }
                onSwitchTab(s.tab);
              }}
              className={`relative flex flex-col items-center gap-1 p-2 rounded-lg text-xs transition-all ${
                isDone ? 'bg-emerald-500 text-white'
                : isCurrent ? 'bg-[#E8613A] text-white shadow-lg scale-105'
                : isLocked ? 'bg-muted/40 text-muted-foreground/60 cursor-not-allowed opacity-60'
                : 'bg-white/60 dark:bg-black/20 text-muted-foreground hover:bg-white'
              }`}
              data-testid={`wizard-step-${s.key}`}
              disabled={isLocked}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDone || isCurrent ? 'bg-white/25' : 'bg-muted'}`}>
                {isDone || isCurrent ? <Icon size={16} /> : isLocked ? <Lock size={14} className="text-muted-foreground/60" /> : <span className="text-xs font-bold text-muted-foreground">{i + 1}</span>}
              </div>
              <span className="text-[10px] font-semibold text-center leading-tight hidden md:block">{s.label}</span>
              <StepStatusIcon done={isDone} current={isCurrent} locked={isLocked} />
            </button>
          );
        })}
      </div>

      {/* Aktuellt steg-kort */}
      <div ref={cardRef} className="bg-white/70 dark:bg-black/20 rounded-lg p-4 border border-white/50 dark:border-white/10">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#E8613A] text-white flex items-center justify-center flex-shrink-0">
            {(() => { const Icon = STEPS[currentIdx].icon; return <Icon size={18} />; })()}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-foreground">{STEPS[currentIdx].label}</h3>
            <p className="text-xs text-muted-foreground mt-1">{STEPS[currentIdx].hint}</p>

            {/* Offert-specifik snabbåtgärd */}
            {STEPS[currentIdx].key === 'quote' && (
              <div className="mt-3 flex flex-wrap gap-2">
                {!quote && !project.quote_generated_at && (
                  <Button
                    size="sm"
                    onClick={handleGenerateQuote}
                    disabled={generatingQuote}
                    className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-8 text-xs"
                    data-testid="wizard-generate-quote"
                  >
                    <Sparkles size={12} className="mr-1" /> {generatingQuote ? 'Genererar…' : 'Generera offert från tid+arbete+material'}
                  </Button>
                )}
                {(quote || project.quote_generated_at) && (
                  <>
                    <Button size="sm" onClick={() => handleSendQuote('email')} disabled={sendingQuote}
                      variant="outline" className="h-8 text-xs" data-testid="wizard-send-quote-email">
                      <Send size={12} className="mr-1" /> Mejla PDF (utan underskrift)
                    </Button>
                    <Button size="sm" onClick={() => handleSendQuote('signed')} disabled={sendingQuote}
                      className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-8 text-xs" data-testid="wizard-send-quote-signed">
                      <Shield size={12} className="mr-1" /> Skicka för signering (BankID/PIN)
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Standard "Öppna"-knapp för alla andra steg */}
            {STEPS[currentIdx].key !== 'quote' && (
              <Button
                size="sm"
                onClick={() => onSwitchTab(STEPS[currentIdx].tab)}
                className="mt-3 bg-[#E8613A] hover:bg-[#D4532F] text-white h-8 text-xs"
                data-testid={`wizard-open-${STEPS[currentIdx].key}`}
              >
                Öppna {STEPS[currentIdx].label} <ChevronRight size={12} className="ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* "Starta projekt"-knapp — visas när alla beräkningar + offert är klara */}
      {(status.material && status.labor && status.time && status.quote) && (
        <div className="mt-4 bg-white dark:bg-black/40 rounded-lg p-4 border-2 border-[#E8613A] shadow-lg" data-testid="start-project-card">
          <div className="flex items-center gap-3">
            <Rocket size={22} className="text-[#E8613A] flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold">Klart att starta projektet!</p>
              <p className="text-xs text-muted-foreground">Efter start är projektet låst för redigering (utom ÄTA-arbeten).</p>
            </div>
            <Button
              onClick={() => setShowStartDialog(true)}
              className="bg-[#E8613A] hover:bg-[#D4532F] text-white"
              data-testid="wizard-start-project-btn"
            >
              <Rocket size={14} className="mr-1.5" /> Starta projekt
            </Button>
          </div>
        </div>
      )}

      {/* Start-dialog med PIN-verifiering */}
      {showStartDialog && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-3" onClick={() => !starting && setShowStartDialog(false)}>
          <div className="bg-card rounded-xl border border-border max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="start-project-dialog">
            <h3 className="text-base font-bold flex items-center gap-2 mb-2">
              <KeyRound size={16} className="text-[#E8613A]" /> Signera start med PIN
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              Efter start blir projektet låst för redigering. Endast ÄTA-arbeten kan läggas till.
            </p>
            <Label className="text-xs">PIN-kod *</Label>
            <Input
              type="password"
              inputMode="numeric"
              value={startPin}
              onChange={(e) => setStartPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
              className="mt-1 mb-4 tracking-widest text-center text-lg font-mono"
              placeholder="••••"
              autoFocus
              data-testid="start-pin-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowStartDialog(false)} className="flex-1" disabled={starting}>Avbryt</Button>
              <Button onClick={handleStartProject} disabled={starting || startPin.length < 4} className="flex-1 bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="confirm-start-project">
                {starting ? <Loader2 size={14} className="animate-spin" /> : '🚀 Starta'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectWizard;
